import { toast } from 'sonner';
import i18next from 'i18next';
import { Logger } from '@/lib/app-log.js';
import { getHttpErrorStatus } from '@/lib/get-http-error-status.js';
import { useBiaImpactConfigurationStore } from '../../store/bia-impact-configuration.store.js';

export class BiaImpactConfigurationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchConfiguration() {
        const store = useBiaImpactConfigurationStore.getState();
        try {
            store.setLoading(true);
            store.setError(null);

            Logger.debug('[BiaImpactConfigurationIntegration] Fetching configuration');
            const response = await this.service.getBiaImpactConfiguration();

            store.setConfiguration(response.data);
            store.setLastUpdated(new Date().toISOString());

            return { success: true, data: response.data };
        } catch (error) {
            const status = getHttpErrorStatus(error);
            if (status === 401) {
                store.setError(null);
                return { success: false, error: 'unauthorized' };
            }
            Logger.error('[BiaImpactConfigurationIntegration] fetch error', error);
            const message = error.message || i18next.t('biaImpactConfiguration:messages.loadError', {
                defaultValue: 'Could not load BIA impact configuration',
            });
            store.setError(message);
            toast.error(message);
            throw error;
        } finally {
            store.setLoading(false);
        }
    }

    notifyValidationError(message) {
        toast.error(
            message || i18next.t('biaImpactConfiguration:messages.validationFailed', {
                defaultValue: 'Configuration validation failed',
            }),
        );
    }

    async updateConfiguration(data) {
        const store = useBiaImpactConfigurationStore.getState();
        try {
            store.setLoading(true);
            store.setError(null);

            Logger.debug('[BiaImpactConfigurationIntegration] Updating configuration');
            const response = await this.service.updateBiaImpactConfiguration(data);

            store.setConfiguration(response.data);
            store.setLastUpdated(new Date().toISOString());
            store.setIsEditing(false);

            toast.success(
                i18next.t('biaImpactConfiguration:messages.saveSuccess', {
                    defaultValue: 'BIA impact scale saved',
                }),
            );

            return { success: true, data: response.data };
        } catch (error) {
            Logger.error('[BiaImpactConfigurationIntegration] update error', error);
            const message = error.message || i18next.t('biaImpactConfiguration:messages.saveError', {
                defaultValue: 'Could not save BIA impact scale',
            });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async resetToDefaults() {
        return this.updateConfiguration({ resetToDefaults: true });
    }
}
